import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, CircularProgress, Alert, IconButton,
  Tooltip, TextField, Switch, FormControlLabel, Collapse, Stack,
  Card, CardContent, Divider, Dialog, DialogTitle,
  DialogContent, DialogActions, Button, InputAdornment,
} from '@mui/material';
import {
  CheckCircle, Cancel, Edit, Save, Close, ExpandMore, ExpandLess,
  Gavel, EuroSymbol, EventNote, Notes, OpenInNew, Delete,
} from '@mui/icons-material';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { API_BASE_URL } from '../../services/api';

const API = API_BASE_URL;

interface Minuta {
  id: string;
  expediente_id: string | null;
  juzgado: string | null;
  ciudad: string | null;
  num_procedimiento: string | null;
  importe_minuta: number | null;
  fecha_presentacion_minuta: string | null;
  requerimiento_solicitado: boolean;
  fecha_requerimiento: string | null;
  monitorio_iniciado: boolean;
  cobrado: boolean;
  fecha_cobro: string | null;
  notas: string | null;
  numero_orden: number | null;
  created_at: string;
  updated_at: string;
  documentos_count?: number;
  estado_respuesta?: string;
  cobro_gerencia_solicitado: boolean;
  fecha_solicitud_cobro_gerencia: string | null;
}

interface EditState {
  requerimiento_solicitado: boolean;
  fecha_requerimiento: string;
  monitorio_iniciado: boolean;
  cobrado: boolean;
  fecha_cobro: string;
  notas: string;
}

function estadoChip(minuta: Minuta) {
  if (minuta.cobrado) return <Chip label="Cobrado" color="success" size="small" icon={<CheckCircle />} />;
  if (minuta.monitorio_iniciado) return <Chip label="Monitorio" color="warning" size="small" />;
  if (minuta.requerimiento_solicitado) return <Chip label="Requerimiento" color="info" size="small" />;
  return <Chip label="Pendiente" color="default" size="small" />;
}

function fmt(date: string | null) {
  if (!date) return '—';
  return new Date(date).toLocaleDateString('es-ES');
}

function fmtEur(n: number | null) {
  if (n === null || n === undefined) return '—';
  return n.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' });
}

export default function MinutasPage() {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [minutas, setMinutas] = useState<Minuta[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editState, setEditState] = useState<EditState>({
    requerimiento_solicitado: false,
    fecha_requerimiento: '',
    monitorio_iniciado: false,
    cobrado: false,
    fecha_cobro: '',
    notas: '',
  });
  const [saving, setSaving] = useState(false);
  const [expandedNota, setExpandedNota] = useState<string | null>(null);
  const [notaDialog, setNotaDialog] = useState<{ id: string; texto: string } | null>(null);

  // Edición de datos administrativos de la minuta (solo admin)
  const [editDialog, setEditDialog] = useState<Minuta | null>(null);
  const [editDatos, setEditDatos] = useState({
    juzgado: '', ciudad: '', num_procedimiento: '',
    importe_minuta: '', fecha_presentacion_minuta: '',
  });
  const [deleteConfirm, setDeleteConfirm] = useState<Minuta | null>(null);

  const fetchMinutas = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/api/minutas`);
      setMinutas(res.data);
    } catch (e: any) {
      setError('Error al cargar las minutas');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchMinutas(); }, [fetchMinutas]);

  const startEdit = (m: Minuta) => {
    setEditingId(m.id);
    setEditState({
      requerimiento_solicitado: m.requerimiento_solicitado,
      fecha_requerimiento: m.fecha_requerimiento ?? '',
      monitorio_iniciado: m.monitorio_iniciado,
      cobrado: m.cobrado,
      fecha_cobro: m.fecha_cobro ?? '',
      notas: m.notas ?? '',
    });
  };

  const cancelEdit = () => setEditingId(null);

  const saveEdit = async (id: string) => {
    setSaving(true);
    try {
      const body: any = {
        requerimiento_solicitado: editState.requerimiento_solicitado,
        monitorio_iniciado: editState.monitorio_iniciado,
        cobrado: editState.cobrado,
        notas: editState.notas || null,
      };
      if (editState.fecha_requerimiento) body.fecha_requerimiento = editState.fecha_requerimiento;
      if (editState.fecha_cobro) body.fecha_cobro = editState.fecha_cobro;

      await axios.put(`${API}/api/minutas/${id}`, body);
      setEditingId(null);
      await fetchMinutas();
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const abrirEditDatos = (m: Minuta) => {
    setEditDatos({
      juzgado: m.juzgado ?? '',
      ciudad: m.ciudad ?? '',
      num_procedimiento: m.num_procedimiento ?? '',
      importe_minuta: m.importe_minuta?.toString() ?? '',
      fecha_presentacion_minuta: m.fecha_presentacion_minuta ?? '',
    });
    setEditDialog(m);
  };

  const guardarDatos = async () => {
    if (!editDialog) return;
    setSaving(true);
    try {
      const body: any = {
        juzgado: editDatos.juzgado || null,
        ciudad: editDatos.ciudad || null,
        num_procedimiento: editDatos.num_procedimiento || null,
        importe_minuta: editDatos.importe_minuta === '' ? null : Number(editDatos.importe_minuta),
        fecha_presentacion_minuta: editDatos.fecha_presentacion_minuta || null,
      };
      await axios.put(`${API}/api/minutas/${editDialog.id}`, body);
      setEditDialog(null);
      await fetchMinutas();
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const eliminarMinuta = async () => {
    if (!deleteConfirm) return;
    setSaving(true);
    try {
      await axios.delete(`${API}/api/minutas/${deleteConfirm.id}`);
      setDeleteConfirm(null);
      await fetchMinutas();
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Error al eliminar');
    } finally {
      setSaving(false);
    }
  };

  // Totales
  const totalImporte = minutas.reduce((s, m) => s + (m.importe_minuta ?? 0), 0);
  const totalCobrado = minutas.filter(m => m.cobrado).reduce((s, m) => s + (m.importe_minuta ?? 0), 0);
  const totalPendiente = totalImporte - totalCobrado;
  const conRequerimiento = minutas.filter(m => m.requerimiento_solicitado && !m.cobrado).length;
  const conMonitorio = minutas.filter(m => m.monitorio_iniciado && !m.cobrado).length;

  if (loading) return (
    <Box display="flex" justifyContent="center" mt={6}><CircularProgress /></Box>
  );

  return (
    <Box>
      {/* Cabecera */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Control de Minutas
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Seguimiento de cobro de honorarios periciales
          </Typography>
        </Box>
      </Box>

      {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert>}

      {/* Tarjetas resumen */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Card variant="outlined" sx={{ flex: '1 1 160px' }}>
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="caption" color="text.secondary">Total minutas</Typography>
            <Typography variant="h6" fontWeight={700}>{fmtEur(totalImporte)}</Typography>
            <Typography variant="caption">{minutas.length} expedientes</Typography>
          </CardContent>
        </Card>
        <Card variant="outlined" sx={{ flex: '1 1 160px', borderColor: 'error.light' }}>
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="caption" color="text.secondary">Pendiente cobro</Typography>
            <Typography variant="h6" fontWeight={700} color="error.main">{fmtEur(totalPendiente)}</Typography>
            <Typography variant="caption">{minutas.filter(m => !m.cobrado).length} pendientes</Typography>
          </CardContent>
        </Card>
        <Card variant="outlined" sx={{ flex: '1 1 160px', borderColor: 'warning.light' }}>
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="caption" color="text.secondary">En gestión</Typography>
            <Typography variant="h6" fontWeight={700} color="warning.main">
              {conRequerimiento} req. / {conMonitorio} mon.
            </Typography>
            <Typography variant="caption">Requerimientos / Monitorios</Typography>
          </CardContent>
        </Card>
        <Card variant="outlined" sx={{ flex: '1 1 160px', borderColor: 'success.light' }}>
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="caption" color="text.secondary">Cobrado</Typography>
            <Typography variant="h6" fontWeight={700} color="success.main">{fmtEur(totalCobrado)}</Typography>
            <Typography variant="caption">{minutas.filter(m => m.cobrado).length} cobrados</Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Tabla */}
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.50' }}>
              <TableCell sx={{ fontWeight: 700, width: 40 }}>#</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Juzgado</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Ciudad</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Procedimiento</TableCell>
              <TableCell sx={{ fontWeight: 700, textAlign: 'right' }}>Importe</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Pres. minuta</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Documentos judiciales</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Respuesta</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Cobro</TableCell>
              <TableCell sx={{ fontWeight: 700, textAlign: 'center' }}>Requerimiento</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>F. Req.</TableCell>
              <TableCell sx={{ fontWeight: 700, textAlign: 'center' }}>Monitorio</TableCell>
              <TableCell sx={{ fontWeight: 700, textAlign: 'center' }}>Estado</TableCell>
              {isAdmin && <TableCell sx={{ fontWeight: 700, textAlign: 'center' }}>Acciones</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {minutas.map((m, idx) => {
              const rowBg = m.cobrado ? '#f0fff4' : m.monitorio_iniciado ? '#fffbf0' : 'inherit';
              return (
                <TableRow key={m.id} sx={{ bgcolor: rowBg, cursor: 'pointer', '&:hover': { bgcolor: m.cobrado ? '#e8f5e9' : '#f5f5f5' } }} onClick={() => navigate(`/minutas/${m.id}`)}>
                  <TableCell>{m.numero_orden ?? idx + 1}</TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {m.juzgado ?? '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>{m.ciudad ?? '—'}</TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>{m.num_procedimiento ?? '—'}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight={600}>{fmtEur(m.importe_minuta)}</Typography>
                  </TableCell>
                  <TableCell>{fmt(m.fecha_presentacion_minuta)}</TableCell>
                  <TableCell align="center">
                    <Chip label={m.documentos_count ?? 0} size="small" variant="outlined" color={m.documentos_count ? 'primary' : 'default'} />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={m.estado_respuesta || 'No consta'}
                      size="small"
                      color={m.estado_respuesta === 'Respuesta recibida' || m.estado_respuesta === 'Resuelta' ? 'success' : m.estado_respuesta === 'Pendiente de revisión' ? 'warning' : 'default'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    {m.cobro_gerencia_solicitado
                      ? <Chip label={`Gerencia · ${fmt(m.fecha_solicitud_cobro_gerencia)}`} size="small" color="info" variant="outlined" />
                      : <Typography variant="body2" color="text.secondary">—</Typography>}
                  </TableCell>
                  <TableCell align="center">
                    {m.requerimiento_solicitado ? (
                      <CheckCircle sx={{ color: 'info.main', fontSize: 20 }} />
                    ) : (
                      <Cancel sx={{ color: 'grey.400', fontSize: 20 }} />
                    )}
                  </TableCell>
                  <TableCell>{fmt(m.fecha_requerimiento)}</TableCell>
                  <TableCell align="center">
                    {m.monitorio_iniciado
                      ? <CheckCircle sx={{ color: 'warning.main', fontSize: 20 }} />
                      : <Cancel sx={{ color: 'grey.400', fontSize: 20 }} />}
                  </TableCell>
                  <TableCell align="center">{estadoChip(m)}</TableCell>
                  {isAdmin && (
                    <TableCell align="center" onClick={e => e.stopPropagation()}>
                      <Tooltip title="Editar datos">
                        <IconButton size="small" onClick={() => abrirEditDatos(m)}>
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Eliminar">
                        <IconButton size="small" onClick={() => setDeleteConfirm(m)} sx={{ color: 'error.main' }}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}

            {/* Fila total */}
            {minutas.length > 0 && (
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell colSpan={4} align="right">
                  <Typography variant="body2" fontWeight={700}>TOTAL</Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" fontWeight={700}>{fmtEur(totalImporte)}</Typography>
                </TableCell>
                <TableCell colSpan={isAdmin ? 6 : 5} />
              </TableRow>
            )}

            {minutas.length === 0 && (
              <TableRow>
                <TableCell colSpan={isAdmin ? 11 : 10} align="center" sx={{ py: 6 }}>
                  <Typography color="text.secondary">
                    No hay minutas registradas todavía.
                    {isAdmin && ' Puedes añadirlas desde el detalle de cada expediente.'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog editar datos de la minuta (admin) */}
      <Dialog open={Boolean(editDialog)} onClose={() => setEditDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Editar datos de la minuta</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Juzgado" fullWidth
              value={editDatos.juzgado}
              onChange={e => setEditDatos(s => ({ ...s, juzgado: e.target.value }))}
            />
            <TextField
              label="Ciudad" fullWidth
              value={editDatos.ciudad}
              onChange={e => setEditDatos(s => ({ ...s, ciudad: e.target.value }))}
            />
            <TextField
              label="Procedimiento" fullWidth
              value={editDatos.num_procedimiento}
              onChange={e => setEditDatos(s => ({ ...s, num_procedimiento: e.target.value }))}
              helperText="Ej. EJ 382/21"
            />
            <TextField
              label="Importe" type="number" fullWidth
              value={editDatos.importe_minuta}
              onChange={e => setEditDatos(s => ({ ...s, importe_minuta: e.target.value }))}
              InputProps={{ startAdornment: <InputAdornment position="start">€</InputAdornment> }}
            />
            <TextField
              label="Fecha presentación" type="date" fullWidth
              value={editDatos.fecha_presentacion_minuta ? editDatos.fecha_presentacion_minuta.slice(0, 10) : ''}
              onChange={e => setEditDatos(s => ({ ...s, fecha_presentacion_minuta: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog(null)}>Cancelar</Button>
          <Button variant="contained" onClick={guardarDatos} disabled={saving}
                  startIcon={saving ? <CircularProgress size={16} /> : <Save />}>
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmación de borrado */}
      <Dialog open={Boolean(deleteConfirm)} onClose={() => setDeleteConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Eliminar minuta</DialogTitle>
        <DialogContent>
          <Typography>
            ¿Eliminar la minuta de <b>{deleteConfirm?.num_procedimiento ?? 'sin número'}</b>?
            Esta acción no se puede deshacer.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={eliminarMinuta} disabled={saving}
                  startIcon={saving ? <CircularProgress size={16} /> : <Delete />}>
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog para editar notas largas */}
      <Dialog open={Boolean(notaDialog)} onClose={() => setNotaDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Notas del expediente</DialogTitle>
        <DialogContent>
          <TextField
            multiline
            rows={5}
            fullWidth
            value={notaDialog?.texto ?? ''}
            onChange={e => {
              setNotaDialog(d => d ? { ...d, texto: e.target.value } : null);
              setEditState(s => ({ ...s, notas: e.target.value }));
            }}
            placeholder="Añade notas sobre la gestión de cobro..."
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNotaDialog(null)}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
